import { API } from "@/config/apiBase";
import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, Plus, Search, Bell, Settings, LogOut, Users, Calendar,
  Flame, Clock, UserPlus, Filter, X, Check, PhoneCall, Edit,
  MoreVertical, Trash2, Mail, Gift, Crown, Share2, Sparkles,
  Upload, FileText, TrendingUp, Cake, Heart, Package, Ticket, Copy, Shield, Wand2, RefreshCw, Brain
} from "lucide-react";
import CallButton from "@/components/CallButton";
import PostCallPrompt from "@/components/PostCallPrompt";
import OnboardingFlow from "@/components/OnboardingFlow";
import GoogleContactsImport from "@/components/GoogleContactsImport";
import CopyButton from "@/components/common/CopyButton";
import AddToHomeScreen from "@/components/AddToHomeScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import axios from "axios";
import { AuthContext } from "@/App";
import { isPaidPlan, canUseAI } from "@/utils/planUtils";
import { trackUpgradeClicked } from "@/utils/analytics";


const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({});
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [nudges, setNudges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterGroup, setFilterGroup] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterBirthday, setFilterBirthday] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [skippedReminderNudge, setSkippedReminderNudge] = useState(null);
  
  // Modal states
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [showLogCall, setShowLogCall] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [selectedNudge, setSelectedNudge] = useState(null);

  // Form states
  const [newContact, setNewContact] = useState({ name: "", phone: "", email: "", reminder_frequency_days: 14, group_id: null, notes: "", birthday: "" });
  const [newGroup, setNewGroup] = useState({ name: "", color: "#E07A5F" });
  const [callLog, setCallLog] = useState({ duration_minutes: 15, experience: "", mood: "happy" });
  const [inviteData, setInviteData] = useState({ email: "", name: "", message: "" });
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [showReferral, setShowReferral] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [lastCallLoggedAt, setLastCallLoggedAt] = useState(null);
  
  // AI Summary states
  const [showAISummaryPrompt, setShowAISummaryPrompt] = useState(false);
  const [aiRoughNotes, setAiRoughNotes] = useState("");
  const [aiSummary, setAiSummary] = useState("");
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [showAISummaryPreview, setShowAISummaryPreview] = useState(false);

  // Check if user has paid plan (uses effective_plan for family member inheritance)
  const isPaidUser = isPaidPlan(user);
  
  // Check if user can use AI features
  const hasAIAccess = canUseAI(user);
  
  // Log Call AI Summary state
  const [logCallSummary, setLogCallSummary] = useState("");
  const [logCallSummaryLoading, setLogCallSummaryLoading] = useState(false);
  
  // Add to Home Screen state - show after first successful call log
  const [showA2HS, setShowA2HS] = useState(false);

  useEffect(() => {
    fetchData();
    fetchAnalytics();
    fetchReferralCode();
    fetchNudges();
    checkAdmin();
    checkSkippedReminderNudge();
  }, []);

  // Check for one-time nudge if user skipped reminder during onboarding
  const checkSkippedReminderNudge = () => {
    try {
      const stored = localStorage.getItem("onboarding_skipped_reminder");
      if (stored) {
        setSkippedReminderNudge(JSON.parse(stored));
      }
    } catch {
      // Ignore parse errors
    }
  };

  const dismissSkippedReminderNudge = () => {
    localStorage.removeItem("onboarding_skipped_reminder");
    setSkippedReminderNudge(null);
  };

  const handleSetupReminderFromNudge = (contactId) => {
    // Navigate to contact detail to set up reminder
    navigate(`/contact/${contactId}`);
    dismissSkippedReminderNudge();
  };

  const fetchData = async () => {
    try {
      const [contactsRes, groupsRes, notificationsRes, statsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/contacts`),
        axios.get(`${API}/groups`),
        axios.get(`${API}/notifications`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/events/upcoming?days=60`)
      ]);
      
      const contactsList = contactsRes.data;
      setContacts(contactsList);
      setGroups(groupsRes.data);
      setNotifications(notificationsRes.data);
      setStats(statsRes.data);
      setUpcomingEvents(eventsRes.data);
      
      // Check if user needs onboarding (0 contacts)
      if (!onboardingChecked) {
        setShowOnboarding(contactsList.length === 0);
        setOnboardingChecked(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNudges = async () => {
    try {
      const response = await axios.get(`${API}/nudges`);
      setNudges(response.data);
    } catch (error) {
      console.error("Error fetching nudges:", error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  const fetchReferralCode = async () => {
    try {
      const response = await axios.get(`${API}/referral/my-code`);
      setReferralCode(response.data);
    } catch (error) {
      console.error("Error fetching referral code:", error);
    }
  };

  const checkAdmin = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`);
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      console.error("Error checking admin:", error);
    }
  };

  const handleRedeemCode = async () => {
    if (!redeemCode.trim()) {
      toast.error("Please enter a code");
      return;
    }
    try {
      const response = await axios.post(`${API}/invite-codes/redeem?code=${redeemCode}`);
      toast.success(response.data.message);
      setRedeemCode("");
      setShowReferral(false);
    } catch (error) {
      // Try as referral code
      try {
        const refResponse = await axios.post(`${API}/referral/redeem?code=${redeemCode}`);
        toast.success(refResponse.data.message);
        setRedeemCode("");
        setShowReferral(false);
      } catch (refError) {
        toast.error(error.response?.data?.detail || "Invalid code");
      }
    }
  };

  const copyReferralCode = () => {
    if (referralCode?.code) {
      navigator.clipboard.writeText(referralCode.code);
      toast.success("Referral code copied!");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Ignore logout API errors - proceed with client-side cleanup
    }
    
    // Use context logout - clears state and redirects immediately
    toast.success("Logged out successfully");
    logout();
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!newContact.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!newContact.phone?.trim()) {
      toast.error("Phone number is required");
      return;
    }
    
    try {
      // Sanitize: convert empty strings to null for optional fields
      const payload = {
        ...newContact,
        email: newContact.email?.trim() || null,
        notes: newContact.notes?.trim() || null,
      };
      const response = await axios.post(`${API}/contacts`, payload);
      setContacts([...contacts, response.data]);
      setShowAddContact(false);
      setNewContact({ name: "", phone: "", email: "", reminder_frequency_days: 14, group_id: null, notes: "" });
      toast.success(`Added ${response.data.name}!`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        // Handle Pydantic validation errors
        if (Array.isArray(detail)) {
          toast.error(detail[0]?.msg || "Validation error");
        } else {
          toast.error(detail);
        }
      } else {
        toast.error("Failed to add contact");
      }
    }
  };

  const handleAddGroup = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/groups`, newGroup);
      setGroups([...groups, response.data]);
      setShowAddGroup(false);
      setNewGroup({ name: "", color: "#E07A5F" });
      toast.success(`Created group "${response.data.name}"!`);
    } catch (error) {
      toast.error("Failed to create group");
    }
  };

  const handleLogCall = async (e) => {
    e.preventDefault();
    if (!selectedContact) return;
    
    try {
      await axios.post(`${API}/calls`, {
        contact_id: selectedContact.contact_id,
        ...callLog
      });
      
      // Update lastCallLoggedAt to prevent post-call prompt
      setLastCallLoggedAt(new Date().toISOString());
      
      // Refresh contacts to get updated streak
      const contactsRes = await axios.get(`${API}/contacts`);
      setContacts(contactsRes.data);
      const statsRes = await axios.get(`${API}/stats`);
      setStats(statsRes.data);
      
      setShowLogCall(false);
      setSelectedContact(null);
      setCallLog({ duration_minutes: 15, experience: "", mood: "happy" });
      // Reset AI summary states
      setShowAISummaryPrompt(false);
      setAiRoughNotes("");
      setAiSummary("");
      setShowAISummaryPreview(false);
      setLogCallSummary(""); // Reset new AI summary
      toast.success(`Logged call with ${selectedContact.name}! 🎉`);
      
      // Trigger Add to Home Screen prompt after first successful call log
      // This is the "value moment" - user just logged their first call
      setTimeout(() => setShowA2HS(true), 1500);
    } catch (error) {
      toast.error("Failed to log call");
    }
  };

  // Generate AI summary for the Log Call modal (new simplified version)
  const generateLogCallSummary = async () => {
    if (!callLog.experience?.trim() || callLog.experience.trim().length < 10) {
      toast.error("Add more notes first (at least 10 characters)");
      return;
    }
    if (!selectedContact) return;
    
    setLogCallSummaryLoading(true);
    try {
      const res = await axios.post(`${API}/ai/post-call-summary`, {
        contact_id: selectedContact.contact_id,
        call_notes: callLog.experience,
        duration_minutes: callLog.duration_minutes
      });
      
      const summaryText = res.data?.content || res.data?.summary || res.data?.message || "";
      setLogCallSummary(summaryText);
      toast.success("Summary generated!");
    } catch (error) {
      console.error("[LogCall AI] Summary failed:", error);
      if (error.response?.status === 429) {
        toast.error("AI rate limit reached. Please try again later.");
      } else if (error.response?.status === 403) {
        toast.error("Upgrade to Plus for AI features");
      } else {
        toast.error(error.response?.data?.detail || "Failed to generate summary");
      }
    } finally {
      setLogCallSummaryLoading(false);
    }
  };

  // AI Post-Call Summary handlers
  const handleGenerateAISummary = async () => {
    if (!aiRoughNotes.trim()) {
      toast.error("Please describe what you talked about first");
      return;
    }
    
    setIsGeneratingSummary(true);
    try {
      const response = await axios.post(`${API}/ai/post-call-summary`, {
        contact_id: selectedContact.contact_id,
        call_notes: aiRoughNotes,
        duration_minutes: callLog.duration_minutes
      });
      
      setAiSummary(response.data.content);
      setShowAISummaryPrompt(false);
      setShowAISummaryPreview(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes("rate limit") || detail?.includes("Rate limit")) {
        toast.error("AI rate limit reached. Try again later.");
      } else if (detail?.includes("Upgrade")) {
        toast.error("Upgrade to use AI features");
      } else {
        toast.error("Failed to generate summary. Try again.");
      }
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleUseSummary = () => {
    setCallLog({ ...callLog, experience: aiSummary });
    setShowAISummaryPreview(false);
    setAiSummary("");
    setAiRoughNotes("");
    toast.success("Summary added to notes!");
  };

  const handleEditSummary = () => {
    // Put AI summary in the notes field for manual editing
    setCallLog({ ...callLog, experience: aiSummary });
    setShowAISummaryPreview(false);
    setAiSummary("");
    setAiRoughNotes("");
  };

  const handleRegenerateSummary = () => {
    setShowAISummaryPreview(false);
    setShowAISummaryPrompt(true);
    setAiSummary("");
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      setContacts(contacts.filter(c => c.contact_id !== contactId));
      toast.success("Contact deleted");
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/invite`, inviteData);
      setShowInvite(false);
      setInviteData({ email: "", name: "", message: "" });
      toast.success(`Invite sent to ${inviteData.email}!`);
    } catch (error) {
      toast.error("Failed to send invite");
    }
  };

  const handleCSVImport = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      toast.error("Please select a CSV file");
      return;
    }
    
    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await axios.post(`${API}/contacts/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const result = response.data;
      setShowCSVImport(false);
      setCsvFile(null);
      
      if (result.imported > 0) {
        toast.success(`Imported ${result.imported} contacts!`);
        fetchData(); // Refresh contacts
      }
      if (result.skipped > 0) {
        toast.info(`Skipped ${result.skipped} duplicates`);
      }
      if (result.errors && result.errors.length > 0) {
        toast.warning(`${result.errors.length} errors occurred`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import contacts");
    } finally {
      setIsImporting(false);
    }
  };

  // Nudge Loop Handlers
  const handleSnoozeNudge = async (nudgeId, hours = 24) => {
    try {
      await axios.post(`${API}/nudges/${nudgeId}/snooze`, { snooze_hours: hours });
      toast.success(`Snoozed for ${hours} hours`);
      fetchNudges();
      setShowSnoozeModal(false);
      setSelectedNudge(null);
    } catch (error) {
      toast.error("Failed to snooze nudge");
    }
  };

  const handleCompleteNudge = async (nudgeId) => {
    try {
      await axios.post(`${API}/nudges/${nudgeId}/done`);
      toast.success("Great job staying in touch! 🎉");
      fetchNudges();
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error("Failed to complete nudge");
    }
  };

  const handleDismissNudge = async (nudgeId) => {
    try {
      await axios.post(`${API}/nudges/${nudgeId}/dismiss`);
      toast.info("Nudge dismissed");
      fetchNudges();
    } catch (error) {
      toast.error("Failed to dismiss nudge");
    }
  };

  const handleCallFromNudge = (nudge) => {
    const contact = contacts.find(c => c.contact_id === nudge.contact_id);
    if (contact) {
      setSelectedContact(contact);
      setShowLogCall(true);
    }
  };

  // Handler for PostCallPrompt - opens log call modal with contact prefilled
  const handlePostCallLog = ({ contactId, contactName }) => {
    const contact = contacts.find(c => c.contact_id === contactId);
    if (contact) {
      setSelectedContact(contact);
      setShowLogCall(true);
    }
  };

  // Handler for completing onboarding
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // Refresh data to get the newly created contact
    fetchData();
    fetchNudges();
    // Check if user skipped reminder and should see nudge
    checkSkippedReminderNudge();
  };

  const getNudgeIcon = (reason) => {
    switch (reason) {
      case "birthday": return "🎂";
      case "event": return "📅";
      case "overdue": return "⏰";
      case "missed_touch": return "💭";
      default: return "📞";
    }
  };

  const getNudgeColor = (reason) => {
    switch (reason) {
      case "birthday": return "bg-[#F2CC8F]/20 border-[#F2CC8F]";
      case "event": return "bg-[#81B29A]/20 border-[#81B29A]";
      case "overdue": return "bg-[#E07A5F]/20 border-[#E07A5F]";
      case "missed_touch": return "bg-[#9B59B6]/20 border-[#9B59B6]";
      default: return "bg-[#3D405B]/10 border-[#3D405B]";
    }
  };

  const getContactStatus = (contact) => {
    if (!contact.next_reminder) return "neutral";
    const nextReminder = new Date(contact.next_reminder);
    const now = new Date();
    if (nextReminder < now) return "overdue";
    const daysUntil = Math.ceil((nextReminder - now) / (1000 * 60 * 60 * 24));
    if (daysUntil <= 3) return "soon";
    return "good";
  };

  // Check if a contact's streak is at risk (has streak, overdue, no recent call)
  const isStreakAtRisk = (contact) => {
    if (!contact.streak || contact.streak === 0) return false;
    if (!contact.next_reminder) return false;
    
    const nextReminder = new Date(contact.next_reminder);
    const now = new Date();
    
    // Streak is at risk if reminder is overdue
    return nextReminder < now;
  };

  // Get contacts with streaks at risk
  const streaksAtRisk = contacts.filter(isStreakAtRisk);

  const getDaysSince = (date) => {
    if (!date) return null;
    const lastCalled = new Date(date);
    const now = new Date();
    return Math.floor((now - lastCalled) / (1000 * 60 * 60 * 24));
  };

  // Get birthday month from date string
  const getBirthdayMonth = (birthday) => {
    if (!birthday) return null;
    return new Date(birthday).getMonth();
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (contact.email && contact.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
                         (contact.phone && contact.phone.includes(searchQuery));
    const matchesGroup = filterGroup === "all" || contact.group_id === filterGroup;
    const status = getContactStatus(contact);
    const matchesStatus = filterStatus === "all" || 
                         (filterStatus === "overdue" && status === "overdue") ||
                         (filterStatus === "good" && (status === "good" || status === "soon"));
    
    // Birthday filter
    let matchesBirthday = true;
    if (filterBirthday !== "all" && contact.birthday) {
      const month = getBirthdayMonth(contact.birthday);
      if (filterBirthday === "this_month") {
        matchesBirthday = month === new Date().getMonth();
      } else if (filterBirthday === "next_month") {
        matchesBirthday = month === (new Date().getMonth() + 1) % 12;
      } else if (filterBirthday === "upcoming") {
        const today = new Date();
        const bday = new Date(contact.birthday);
        bday.setFullYear(today.getFullYear());
        if (bday < today) bday.setFullYear(today.getFullYear() + 1);
        const daysUntil = Math.ceil((bday - today) / (1000 * 60 * 60 * 24));
        matchesBirthday = daysUntil <= 30;
      }
    } else if (filterBirthday !== "all" && !contact.birthday) {
      matchesBirthday = false;
    }
    
    return matchesSearch && matchesGroup && matchesStatus && matchesBirthday;
  });

  const unreadNotifications = notifications.filter(n => !n.read).length;

  // Handle notification click - mark as read and navigate if applicable
  const handleNotificationClick = async (notif) => {
    // Mark as read immediately in UI
    setNotifications(prev => 
      prev.map(n => n.notification_id === notif.notification_id ? { ...n, read: true } : n)
    );
    
    // Close dropdown
    setShowNotifications(false);
    
    // Mark as read on backend
    try {
      await axios.put(`${API}/notifications/${notif.notification_id}/read`);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
    
    // Navigate based on notification type
    if (notif.contact_id) {
      // Go to contact detail for contact-related notifications
      navigate(`/contact/${notif.contact_id}`);
    }
    // For notifications without contact_id (system notifications), just mark as read
  };

  // Mark all notifications as read
  const handleMarkAllRead = async () => {
    // Update UI immediately
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    
    // Update backend
    try {
      await axios.put(`${API}/notifications/read-all`);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#E07A5F] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Show onboarding for new users with 0 contacts
  if (showOnboarding) {
    return (
      <OnboardingFlow
        onComplete={handleOnboardingComplete}
        userName={user?.name}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Add to Home Screen Banner */}
      <AddToHomeScreen 
        show={showA2HS} 
        onDismiss={() => setShowA2HS(false)} 
      />
      
      {/* Header */}
      <header className="bg-white border-b border-[#F4F1EA] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E07A5F] rounded-full flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-[#3D405B] hidden sm:block" style={{ fontFamily: 'Fraunces, serif' }}>
                OuttaTouch
              </span>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              {/* Notifications */}
              <DropdownMenu open={showNotifications} onOpenChange={setShowNotifications}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
                    <Bell className="w-5 h-5 text-[#6D6F7C]" />
                    {unreadNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#E07A5F] text-white text-xs rounded-full flex items-center justify-center notification-pulse">
                        {unreadNotifications}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 bg-white border-[#F4F1EA] rounded-2xl p-2">
                  <div className="p-3 border-b border-[#F4F1EA] flex items-center justify-between">
                    <h3 className="font-semibold text-[#3D405B]">Notifications</h3>
                    {unreadNotifications > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-[#81B29A] hover:text-[#71A28A] font-medium"
                        data-testid="mark-all-read-btn"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {unreadNotifications === 0 ? (
                      <p className="text-center text-[#9CA3AF] py-8">All caught up! 🎉</p>
                    ) : (
                      notifications.filter(n => !n.read).slice(0, 10).map((notif) => (
                        <button
                          key={notif.notification_id}
                          onClick={() => handleNotificationClick(notif)}
                          className="w-full text-left p-3 hover:bg-[#F4F1EA] rounded-xl transition-colors cursor-pointer"
                          data-testid={`notification-${notif.notification_id}`}
                        >
                          <div className="flex items-start gap-3">
                            <span className="w-2 h-2 bg-[#E07A5F] rounded-full mt-1.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm text-[#3D405B] font-medium">
                                {notif.title}
                              </p>
                              <p className="text-xs text-[#9CA3AF] mt-1">{notif.message}</p>
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Settings */}
              <Link to="/settings" data-testid="settings-link">
                <Button variant="ghost" size="icon">
                  <Settings className="w-5 h-5 text-[#6D6F7C]" />
                </Button>
              </Link>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-button">
                    <div className="w-8 h-8 bg-[#81B29A] rounded-full flex items-center justify-center text-white font-semibold">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    <span className="hidden sm:block text-[#3D405B] font-medium">{user?.name?.split(" ")[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border-[#F4F1EA] rounded-2xl">
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowReferral(true)} className="flex items-center gap-2">
                    <Ticket className="w-4 h-4" />
                    Referral Code
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2 text-[#E07A5F]">
                        <Shield className="w-4 h-4" />
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout} className="text-[#E07A5F]" data-testid="logout-button">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* One-time nudge for users who skipped reminder during onboarding */}
      {skippedReminderNudge && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-[#F2CC8F]/20 to-[#E07A5F]/10 border border-[#F2CC8F]/30 rounded-2xl p-4 flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#F2CC8F]/30 rounded-full flex items-center justify-center">
                <Bell className="w-5 h-5 text-[#E07A5F]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#3D405B]">
                  Set up a reminder for {skippedReminderNudge.contact_name}?
                </p>
                <p className="text-xs text-[#6D6F7C]">
                  We&apos;ll remind you when it&apos;s time to reconnect
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => handleSetupReminderFromNudge(skippedReminderNudge.contact_id)}
                className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full text-xs"
                data-testid="setup-reminder-nudge-btn"
              >
                Set reminder
              </Button>
              <button
                onClick={dismissSkippedReminderNudge}
                className="p-1 text-[#6D6F7C] hover:text-[#3D405B] rounded-full hover:bg-white/50"
                aria-label="Dismiss"
                data-testid="dismiss-reminder-nudge-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Welcome & Stats */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#3D405B] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
            Hey, {user?.name?.split(" ")[0]}! 👋
          </h1>
          <p className="text-[#6D6F7C]">Keep nurturing your relationships</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div 
            className="card-stat"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#E07A5F]/10 rounded-2xl flex items-center justify-center">
                <Users className="w-6 h-6 text-[#E07A5F]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#3D405B]" data-testid="total-contacts">{stats.total_contacts || 0}</p>
                <p className="text-sm text-[#6D6F7C]">Contacts</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="card-stat"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#81B29A]/10 rounded-2xl flex items-center justify-center">
                <Check className="w-6 h-6 text-[#81B29A]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#3D405B]" data-testid="connected-count">{stats.connected_count || 0}</p>
                <p className="text-sm text-[#6D6F7C]">Connected</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="card-stat"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#F2CC8F]/20 rounded-2xl flex items-center justify-center">
                <Flame className="w-6 h-6 text-[#E07A5F] streak-fire" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#3D405B]" data-testid="total-streak">{stats.total_streak || 0}</p>
                <p className="text-sm text-[#6D6F7C]">Total Streak</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="card-stat"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#E07A5F]/10 rounded-2xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#E07A5F]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#E07A5F]" data-testid="overdue-count">{stats.overdue_count || 0}</p>
                <p className="text-sm text-[#6D6F7C]">Overdue</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Streaks at Risk Warning */}
        {streaksAtRisk.length > 0 && (
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.42 }}
          >
            <div className="bg-gradient-to-r from-[#FEF3C7] to-[#FDE68A] border-2 border-[#F59E0B]/30 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#F59E0B]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Flame className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[#92400E] flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                    ⚠️ Streak{streaksAtRisk.length > 1 ? 's' : ''} at Risk
                  </h3>
                  <p className="text-sm text-[#A16207] mt-1">
                    One quick call keeps {streaksAtRisk.length > 1 ? 'these streaks' : 'this streak'} alive.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mt-3">
                    {streaksAtRisk.slice(0, 3).map((contact) => (
                      <div
                        key={contact.contact_id}
                        className="bg-white/70 rounded-xl px-3 py-2 flex items-center gap-2 border border-[#F59E0B]/20"
                        data-testid={`streak-risk-${contact.contact_id}`}
                      >
                        <div className="w-8 h-8 bg-gradient-to-br from-[#E07A5F] to-[#F2CC8F] rounded-lg flex items-center justify-center text-white text-sm font-bold">
                          {contact.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-[#3D405B] text-sm truncate">{contact.name}</p>
                          <p className="text-xs text-[#A16207] flex items-center gap-1">
                            <Flame className="w-3 h-3" />
                            {contact.streak} week streak
                          </p>
                        </div>
                        <CallButton
                          phone={contact.phone}
                          contactId={contact.contact_id}
                          contactName={contact.name}
                          variant="primary"
                          size="sm"
                          label="Call"
                          className="ml-2"
                          data-testid={`streak-risk-call-${contact.contact_id}`}
                        />
                      </div>
                    ))}
                    {streaksAtRisk.length > 3 && (
                      <div className="bg-white/50 rounded-xl px-3 py-2 flex items-center text-sm text-[#A16207]">
                        +{streaksAtRisk.length - 3} more
                      </div>
                    )}
                  </div>
                  
                  {streaksAtRisk.length === 1 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="mt-2 text-[#A16207] hover:text-[#92400E] hover:bg-[#F59E0B]/10 rounded-full text-xs"
                      onClick={() => handleSnoozeNudge(nudges.find(n => n.contact_id === streaksAtRisk[0].contact_id)?.nudge_id, 24)}
                      data-testid="streak-remind-tomorrow"
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      Remind me tomorrow
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Nudge Loop - To Call Section */}
        {nudges.length > 0 && (
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-[#3D405B] flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                <Phone className="w-5 h-5 text-[#E07A5F]" />
                To Call
                <Badge className="bg-[#E07A5F] text-white">{nudges.length}</Badge>
              </h3>
            </div>
            <p className="text-xs text-[#6D6F7C] mb-4">Gentle nudges to help you stay connected.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="nudges-grid">
              {nudges.slice(0, 6).map((nudge, index) => (
                <motion.div
                  key={nudge.nudge_id}
                  className={`rounded-2xl border-2 p-4 ${getNudgeColor(nudge.reason)}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`nudge-card-${nudge.nudge_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getNudgeIcon(nudge.reason)}</span>
                      <div>
                        <h4 className="font-semibold text-[#3D405B]">{nudge.contact_name}</h4>
                        <p className="text-xs text-[#6D6F7C]">
                          {nudge.reason === "birthday" && "Birthday coming up!"}
                          {nudge.reason === "event" && `Event: ${nudge.event_type || "Special occasion"}`}
                          {nudge.reason === "overdue" && `${nudge.days_overdue || 0} days overdue`}
                          {nudge.reason === "reminder" && "Time to reconnect"}
                          {nudge.reason === "missed_touch" && "You haven't checked in yet"}
                        </p>
                      </div>
                    </div>
                    {nudge.snooze_count > 0 && (
                      <Badge variant="outline" className="text-xs border-[#6D6F7C] text-[#6D6F7C]">
                        Snoozed {nudge.snooze_count}x
                      </Badge>
                    )}
                  </div>
                  
                  {nudge.last_called && (
                    <p className="text-xs text-[#6D6F7C] mb-3">
                      Last called: {new Date(nudge.last_called).toLocaleDateString()}
                    </p>
                  )}
                  
                  <div className="flex gap-2">
                    <CallButton
                      phone={contacts.find(c => c.contact_id === nudge.contact_id)?.phone}
                      contactId={nudge.contact_id}
                      contactName={nudge.contact_name}
                      variant="primary"
                      size="sm"
                      label="Call now"
                      className="flex-1"
                      data-testid={`nudge-call-${nudge.nudge_id}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#6D6F7C]/30 text-[#6D6F7C] rounded-full hover:bg-[#F4F1EA]"
                      onClick={() => {
                        setSelectedNudge(nudge);
                        setShowSnoozeModal(true);
                      }}
                      data-testid={`nudge-snooze-${nudge.nudge_id}`}
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      Snooze
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-[#81B29A] rounded-full"
                      onClick={() => handleCompleteNudge(nudge.nudge_id)}
                      data-testid={`nudge-done-${nudge.nudge_id}`}
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
            
            {nudges.length > 6 && (
              <p className="text-center text-sm text-[#6D6F7C] mt-4">
                And {nudges.length - 6} more contacts to call...
              </p>
            )}
          </motion.div>
        )}

        {/* Analytics Widget */}
        {analytics && (
          <motion.div 
            className="card-contact mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#3D405B] flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                <TrendingUp className="w-5 h-5 text-[#E07A5F]" />
                This Month
              </h3>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-[#F4F1EA] rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-[#3D405B]">{analytics.calls_this_month}</p>
                <p className="text-xs text-[#6D6F7C]">Calls Made</p>
              </div>
              <div className="bg-[#81B29A]/10 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-[#81B29A]">{analytics.active_streaks}</p>
                <p className="text-xs text-[#6D6F7C]">Active Streaks</p>
              </div>
              <div className="bg-[#E07A5F]/10 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-[#E07A5F]">{analytics.highest_streak}🔥</p>
                <p className="text-xs text-[#6D6F7C]">Best Streak</p>
              </div>
            </div>

            {analytics.upcoming_birthdays?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#F4F1EA]">
                <p className="text-sm font-medium text-[#6D6F7C] mb-2 flex items-center gap-1">
                  <Cake className="w-4 h-4" /> Upcoming Birthdays
                </p>
                <div className="flex flex-wrap gap-2">
                  {analytics.upcoming_birthdays.map((bday, i) => (
                    <Link key={i} to={`/contact/${bday.contact_id}`}>
                      <Badge className="bg-[#F2CC8F]/20 text-[#3D405B] hover:bg-[#F2CC8F]/30">
                        {bday.name} - {bday.days_until === 0 ? 'Today!' : `${bday.days_until}d`}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
            <Input
              type="text"
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl h-12"
              data-testid="search-input"
            />
          </div>
          
          <div className="flex gap-2">
            <Select value={filterGroup} onValueChange={setFilterGroup}>
              <SelectTrigger className="w-40 bg-white border-2 border-[#F4F1EA] rounded-xl h-12" data-testid="filter-group">
                <SelectValue placeholder="All Groups" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                <SelectItem value="all">All Groups</SelectItem>
                {groups.map(group => (
                  <SelectItem key={group.group_id} value={group.group_id}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color }}></div>
                      {group.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32 bg-white border-2 border-[#F4F1EA] rounded-xl h-12" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="good">On Track</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterBirthday} onValueChange={setFilterBirthday}>
              <SelectTrigger className="w-40 bg-white border-2 border-[#F4F1EA] rounded-xl h-12" data-testid="filter-birthday">
                <Cake className="w-4 h-4 mr-2 text-[#6D6F7C]" />
                <SelectValue placeholder="Birthdays" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                <SelectItem value="all">All Birthdays</SelectItem>
                <SelectItem value="upcoming">Next 30 Days</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="next_month">Next Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Button 
            onClick={() => setShowAddContact(true)}
            className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full"
            data-testid="add-contact-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Contact
          </Button>
          
          <Button 
            onClick={() => setShowCSVImport(true)}
            variant="outline"
            className="border-2 border-[#F4F1EA] hover:bg-[#F4F1EA] rounded-full text-[#3D405B]"
            data-testid="csv-import-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </Button>
          
          <Button 
            onClick={() => setShowAddGroup(true)}
            variant="outline"
            className="border-2 border-[#F4F1EA] hover:bg-[#F4F1EA] rounded-full text-[#3D405B]"
            data-testid="add-group-button"
          >
            <Users className="w-4 h-4 mr-2" />
            New Group
          </Button>
          
          <Button 
            onClick={() => setShowInvite(true)}
            variant="outline"
            className="border-2 border-[#F4F1EA] hover:bg-[#F4F1EA] rounded-full text-[#3D405B]"
            data-testid="invite-button"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Friend
          </Button>
          
          <Link to="/gifts">
            <Button 
              variant="outline"
              className="border-2 border-[#F4F1EA] hover:bg-[#F4F1EA] rounded-full text-[#3D405B] relative"
              data-testid="gift-shop-button"
            >
              <Gift className="w-4 h-4 mr-2" />
              Gift Shop
              <span className="ml-2 text-[10px] bg-[#F2CC8F]/30 text-[#E07A5F] px-1.5 py-0.5 rounded-full font-medium">
                Soon
              </span>
            </Button>
          </Link>
          
          <Link to="/calendar">
            <Button 
              variant="outline"
              className="border-2 border-[#F4F1EA] hover:bg-[#F4F1EA] rounded-full text-[#3D405B]"
              data-testid="calendar-button"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Occasions
            </Button>
          </Link>
          
          {!isPaidUser && (
            <Link to="/pricing">
              <Button 
                className="bg-gradient-to-r from-[#F2CC8F] to-[#E07A5F] hover:opacity-90 text-white rounded-full"
                data-testid="upgrade-button"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade
              </Button>
            </Link>
          )}
        </div>

        {/* Upcoming Events Banner */}
        {upcomingEvents.length > 0 && (
          <motion.div 
            className="mb-8 bg-gradient-to-r from-[#F2CC8F]/20 to-[#E07A5F]/20 rounded-2xl p-4"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#3D405B] flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#E07A5F]" />
                Upcoming in Next 2 Weeks
              </h3>
              <Link to="/calendar">
                <Button variant="ghost" size="sm" className="text-[#E07A5F]">
                  View All
                </Button>
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {upcomingEvents.slice(0, 5).map((event, index) => (
                <motion.div
                  key={event.event_id || event.holiday_id || `${event.contact_id}-${event.event_type}-${index}`}
                  className="flex-shrink-0 bg-white rounded-xl p-3 border border-[#F4F1EA] min-w-[200px]"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{event.event_icon}</span>
                    <div>
                      <p className="font-medium text-[#3D405B] text-sm">
                        {event.source === "holiday" 
                          ? (event.contact_name 
                              ? `${event.event_name} — ${event.contact_name}`
                              : event.event_name)
                          : event.contact_name}
                      </p>
                      <p className="text-xs text-[#6D6F7C]">
                        {event.source === "holiday" 
                          ? (event.needs_contacts 
                              ? "Add people to remind"
                              : (event.days_until === 0 ? "Today!" : event.days_until === 1 ? "Tomorrow" : `In ${event.days_until} days`))
                          : `${event.event_name} • ${event.days_until === 0 ? "Today!" : event.days_until === 1 ? "Tomorrow" : `In ${event.days_until} days`}`
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Contacts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="contacts-grid">
          <AnimatePresence>
            {filteredContacts.length === 0 ? (
              <div className="col-span-full text-center py-16">
                <div className="w-20 h-20 bg-[#F4F1EA] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-[#9CA3AF]" />
                </div>
                <h3 className="text-xl font-semibold text-[#3D405B] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  {contacts.length === 0 ? "No contacts yet" : "No matches found"}
                </h3>
                <p className="text-[#6D6F7C] mb-4 max-w-sm mx-auto">
                  {contacts.length === 0 
                    ? "Add someone you care about and we'll help you stay in touch." 
                    : "Try adjusting your search or filters."}
                </p>
                {contacts.length === 0 && (
                  <Button 
                    onClick={() => setShowAddContact(true)}
                    className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full"
                    data-testid="add-first-contact-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Contact
                  </Button>
                )}
              </div>
            ) : (
              filteredContacts.map((contact, index) => {
                const status = getContactStatus(contact);
                const daysSince = getDaysSince(contact.last_called);
                const group = groups.find(g => g.group_id === contact.group_id);
                
                return (
                  <motion.div
                    key={contact.contact_id}
                    className="card-contact card-lift cursor-pointer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => navigate(`/contact/${contact.contact_id}`)}
                    data-testid={`contact-card-${contact.contact_id}`}
                  >
                    {/* Status indicator */}
                    <div className={`absolute top-4 right-4 w-3 h-3 rounded-full ${
                      status === "overdue" ? "bg-[#E07A5F]" :
                      status === "soon" ? "bg-[#F2CC8F]" : "bg-[#81B29A]"
                    }`}></div>

                    {/* Avatar & Name */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#E07A5F] to-[#F2CC8F] rounded-2xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                        {contact.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#3D405B] text-lg truncate" style={{ fontFamily: 'Fraunces, serif' }}>
                          {contact.name}
                        </h3>
                        {group && (
                          <Badge variant="secondary" className="mt-1 bg-[#F4F1EA] text-[#6D6F7C] text-xs">
                            <div className="w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: group.color }}></div>
                            {group.name}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#6D6F7C]">Last called</span>
                        <span className={`font-medium ${status === "overdue" ? "text-[#E07A5F]" : "text-[#3D405B]"}`}>
                          {daysSince !== null ? `${daysSince} days ago` : "Never"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#6D6F7C]">Streak</span>
                        <span className="flex items-center gap-1 font-medium text-[#3D405B]">
                          {contact.streak > 0 && <Flame className={`w-4 h-4 ${isStreakAtRisk(contact) ? 'text-[#F59E0B]' : 'text-[#E07A5F]'} streak-fire`} />}
                          {contact.streak}
                          {isStreakAtRisk(contact) && (
                            <span className="text-[10px] text-[#F59E0B] font-semibold ml-1" data-testid={`streak-at-risk-badge-${contact.contact_id}`}>
                              ⚠️
                            </span>
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <CallButton
                        phone={contact.phone}
                        contactId={contact.contact_id}
                        contactName={contact.name}
                        variant="icon"
                        size="sm"
                        data-testid={`call-${contact.contact_id}`}
                      />
                      <Button
                        size="sm"
                        className="flex-1 bg-[#81B29A] hover:bg-[#71A28A] text-white rounded-full"
                        onClick={() => {
                          setSelectedContact(contact);
                          setShowLogCall(true);
                        }}
                        data-testid={`log-call-${contact.contact_id}`}
                      >
                        <PhoneCall className="w-4 h-4 mr-1" />
                        Log Call
                      </Button>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" className="px-2">
                            <MoreVertical className="w-4 h-4 text-[#6D6F7C]" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-white border-[#F4F1EA] rounded-xl">
                          <DropdownMenuItem onClick={() => navigate(`/contact/${contact.contact_id}`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            View / Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteContact(contact.contact_id)}
                            className="text-[#E07A5F]"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </motion.div>
                );
              })
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Add Contact Modal */}
      <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA] max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-2xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Add New Contact
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto flex-1 -webkit-overflow-scrolling-touch pr-1">
          {/* Google Import Option */}
          <div className="mb-4">
            <GoogleContactsImport 
              onImportComplete={(result) => {
                setShowAddContact(false);
                fetchContacts();
                toast.success(`Imported ${result.imported_count} contacts. Skipped ${result.skipped_count} duplicates.`);
              }}
            />
          </div>
          
          <div className="flex items-center gap-3 my-2">
            <Separator className="flex-1" />
            <span className="text-xs text-[#6D6F7C]">or add manually</span>
            <Separator className="flex-1" />
          </div>
          
          <form onSubmit={handleAddContact} className="space-y-4">
            <div>
              <Label className="text-[#3D405B]">Name *</Label>
              <Input
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                placeholder="John Doe"
                required
                data-testid="new-contact-name"
              />
            </div>
            <div>
              <Label className="text-[#3D405B]">Phone *</Label>
              <Input
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                placeholder="+1 234 567 8900"
                required
                data-testid="new-contact-phone"
              />
            </div>
            <div>
              <Label className="text-[#3D405B]">Email (optional)</Label>
              <Input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                placeholder="john@example.com"
                data-testid="new-contact-email"
              />
            </div>
            <div>
              <Label className="text-[#3D405B]">Remind me every</Label>
              <Select 
                value={String(newContact.reminder_frequency_days)} 
                onValueChange={(v) => setNewContact({ ...newContact, reminder_frequency_days: parseInt(v) })}
              >
                <SelectTrigger className="bg-white border-2 border-[#F4F1EA] rounded-xl" data-testid="new-contact-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                  <SelectItem value="7">Weekly</SelectItem>
                  <SelectItem value="14">Every 2 weeks</SelectItem>
                  <SelectItem value="30">Monthly</SelectItem>
                  <SelectItem value="60">Every 2 months</SelectItem>
                  <SelectItem value="90">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {groups.length > 0 && (
              <div>
                <Label className="text-[#3D405B]">Group</Label>
                <Select 
                  value={newContact.group_id || "none"} 
                  onValueChange={(v) => setNewContact({ ...newContact, group_id: v === "none" ? null : v })}
                >
                  <SelectTrigger className="bg-white border-2 border-[#F4F1EA] rounded-xl" data-testid="new-contact-group">
                    <SelectValue placeholder="Select group" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                    <SelectItem value="none">No group</SelectItem>
                    {groups.map(group => (
                      <SelectItem key={group.group_id} value={group.group_id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color }}></div>
                          {group.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[#3D405B]">Birthday 🎂</Label>
              <Input
                type="date"
                value={newContact.birthday}
                onChange={(e) => setNewContact({ ...newContact, birthday: e.target.value })}
                className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                data-testid="new-contact-birthday"
              />
            </div>
            <div>
              <Label className="text-[#3D405B]">Notes</Label>
              <Textarea
                value={newContact.notes}
                onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                placeholder="Any notes about this contact..."
                data-testid="new-contact-notes"
              />
            </div>
            <div className="flex gap-3 pt-4 sticky bottom-0 bg-white pb-1">
              <Button type="button" variant="outline" onClick={() => setShowAddContact(false)} className="flex-1 rounded-full border-[#F4F1EA]">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full" data-testid="save-contact-button">
                Add Contact
              </Button>
            </div>
          </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Group Modal */}
      <Dialog open={showAddGroup} onOpenChange={setShowAddGroup}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Create New Group
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddGroup} className="space-y-4">
            <div>
              <Label className="text-[#3D405B]">Group Name *</Label>
              <Input
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                placeholder="Family, Friends, Work..."
                required
                data-testid="new-group-name"
              />
            </div>
            <div>
              <Label className="text-[#3D405B]">Color</Label>
              <div className="flex gap-2 mt-2">
                {["#E07A5F", "#81B29A", "#F2CC8F", "#3D405B", "#6D6F7C"].map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewGroup({ ...newGroup, color })}
                    className={`w-10 h-10 rounded-xl transition-all ${newGroup.color === color ? 'ring-2 ring-offset-2 ring-[#3D405B]' : ''}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddGroup(false)} className="flex-1 rounded-full border-[#F4F1EA]">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full" data-testid="save-group-button">
                Create Group
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Log Call Modal */}
      <Dialog open={showLogCall} onOpenChange={(open) => { 
        setShowLogCall(open); 
        if (!open) {
          setSelectedContact(null);
          // Reset AI summary states when modal closes
          setShowAISummaryPrompt(false);
          setAiRoughNotes("");
          setAiSummary("");
          setShowAISummaryPreview(false);
          setLogCallSummary(""); // Reset new AI summary
        }
      }}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA] max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Log Call with {selectedContact?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogCall} className="space-y-4">
            <div>
              <Label className="text-[#3D405B]">Duration (minutes)</Label>
              <Input
                type="number"
                value={callLog.duration_minutes}
                onChange={(e) => setCallLog({ ...callLog, duration_minutes: parseInt(e.target.value) || 0 })}
                className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                min="1"
                data-testid="call-duration"
              />
            </div>
            <div>
              <Label className="text-[#3D405B]">How did it go?</Label>
              <div className="flex gap-2 mt-2">
                {[
                  { value: "happy", emoji: "😊", label: "Great" },
                  { value: "neutral", emoji: "😐", label: "Okay" },
                  { value: "sad", emoji: "😔", label: "Tough" }
                ].map(mood => (
                  <button
                    key={mood.value}
                    type="button"
                    onClick={() => setCallLog({ ...callLog, mood: mood.value })}
                    className={`flex-1 py-3 rounded-xl border-2 transition-all ${
                      callLog.mood === mood.value 
                        ? 'border-[#E07A5F] bg-[#E07A5F]/5' 
                        : 'border-[#F4F1EA] hover:border-[#E07A5F]/50'
                    }`}
                  >
                    <span className="text-2xl">{mood.emoji}</span>
                    <p className="text-sm text-[#6D6F7C] mt-1">{mood.label}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[#3D405B]">Experience / Notes</Label>
                {!showAISummaryPrompt && !showAISummaryPreview && (
                  <div className="relative group">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (isPaidUser) {
                          setShowAISummaryPrompt(true);
                        } else {
                          navigate("/pricing");
                        }
                      }}
                      className={`rounded-full text-xs ${
                        isPaidUser 
                          ? "text-[#E07A5F] hover:text-[#D0694E] hover:bg-[#E07A5F]/5" 
                          : "text-[#9B59B6] hover:text-[#8E44AD] hover:bg-[#9B59B6]/5"
                      }`}
                      data-testid="ai-summary-btn"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      {isPaidUser ? "Generate with AI" : "Upgrade for AI"}
                      {!isPaidUser && <Crown className="w-3 h-3 ml-1" />}
                    </Button>
                  </div>
                )}
              </div>
              
              {/* AI Summary Prompt - Enter rough notes */}
              {showAISummaryPrompt && (
                <div className="bg-[#F4F1EA]/50 rounded-xl p-4 space-y-3 mb-3">
                  <p className="text-sm text-[#3D405B] font-medium">
                    Briefly describe what you talked about
                  </p>
                  <p className="text-xs text-[#6D6F7C]">
                    Rough notes are fine — AI will clean it up
                  </p>
                  <Textarea
                    value={aiRoughNotes}
                    onChange={(e) => setAiRoughNotes(e.target.value)}
                    className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                    placeholder="e.g. talked about her new job, she's stressed about the move, mentioned wanting to visit in spring..."
                    rows={3}
                    data-testid="ai-rough-notes"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowAISummaryPrompt(false);
                        setAiRoughNotes("");
                      }}
                      className="rounded-full border-[#F4F1EA]"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleGenerateAISummary}
                      disabled={isGeneratingSummary || !aiRoughNotes.trim()}
                      className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full"
                      data-testid="generate-summary-btn"
                    >
                      {isGeneratingSummary ? (
                        <>
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-3 h-3 mr-1" />
                          Generate
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
              
              {/* AI Summary Preview */}
              {showAISummaryPreview && aiSummary && (
                <div className="bg-gradient-to-br from-[#E07A5F]/5 to-[#81B29A]/5 rounded-xl p-4 space-y-3 mb-3 border border-[#E07A5F]/20">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#E07A5F]" />
                    <span className="text-sm font-medium text-[#3D405B]">AI Summary</span>
                  </div>
                  <p className="text-sm text-[#3D405B] whitespace-pre-wrap">{aiSummary}</p>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleUseSummary}
                      className="bg-[#81B29A] hover:bg-[#71A28A] text-white rounded-full"
                      data-testid="use-summary-btn"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Use summary
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleEditSummary}
                      className="rounded-full border-[#F4F1EA]"
                      data-testid="edit-summary-btn"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRegenerateSummary}
                      className="text-[#6D6F7C] rounded-full"
                      data-testid="regenerate-summary-btn"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Regenerate
                    </Button>
                  </div>
                </div>
              )}
              
              <Textarea
                value={callLog.experience}
                onChange={(e) => setCallLog({ ...callLog, experience: e.target.value })}
                className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                placeholder="What did you talk about? Any memorable moments?"
                rows={4}
                data-testid="call-experience"
              />
              
              {/* AI Summary Section - Direct in modal */}
              {hasAIAccess && !showAISummaryPrompt && !showAISummaryPreview && (
                <div className="pt-3 border-t border-[#F4F1EA]">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2 text-sm">
                      <Sparkles className="w-4 h-4 text-[#E07A5F]" />
                      AI Summary
                    </Label>
                    {!logCallSummary && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={generateLogCallSummary}
                        disabled={logCallSummaryLoading || !callLog.experience?.trim() || callLog.experience.trim().length < 10}
                        className="border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F]/10 rounded-full text-xs"
                        data-testid="generate-summary-inline-btn"
                      >
                        {logCallSummaryLoading ? (
                          <>
                            <Brain className="w-3 h-3 mr-1 animate-pulse" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-3 h-3 mr-1" />
                            Generate Summary
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  
                  {!logCallSummary && (!callLog.experience?.trim() || callLog.experience.trim().length < 10) && (
                    <p className="text-xs text-[#6D6F7C]">Add at least 10 characters of notes to generate an AI summary</p>
                  )}
                  
                  {/* Generated Summary Preview */}
                  {logCallSummary && (
                    <div className="mt-2 p-3 bg-gradient-to-br from-[#E07A5F]/5 to-[#81B29A]/5 rounded-xl border border-[#E07A5F]/20">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-medium text-[#3D405B]">AI Generated Summary</span>
                        <CopyButton 
                          text={logCallSummary}
                          label="Copy"
                          className="text-xs"
                          size="sm"
                        />
                      </div>
                      <p className="text-sm text-[#3D405B] whitespace-pre-wrap mb-3">{logCallSummary}</p>
                      
                      {/* Action buttons */}
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            // Merge summary into notes
                            const existingNotes = callLog.experience?.trim() || "";
                            const mergedNotes = existingNotes 
                              ? `AI Summary:\n${logCallSummary}\n\nNotes:\n${existingNotes}`
                              : logCallSummary;
                            setCallLog({ ...callLog, experience: mergedNotes });
                            setLogCallSummary("");
                            toast.success("Summary added to notes!");
                          }}
                          className="bg-[#81B29A] hover:bg-[#71A28A] text-white rounded-full"
                          data-testid="use-summary-inline-btn"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Save to log
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setLogCallSummary("")}
                          className="rounded-full border-[#F4F1EA]"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Discard
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={generateLogCallSummary}
                          disabled={logCallSummaryLoading}
                          className="text-[#6D6F7C] rounded-full"
                        >
                          <Wand2 className="w-3 h-3 mr-1" />
                          Regenerate
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowLogCall(false)} className="flex-1 rounded-full border-[#F4F1EA]">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#81B29A] hover:bg-[#71A28A] text-white rounded-full" data-testid="save-call-button">
                <Check className="w-4 h-4 mr-2" />
                Log Call
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite Modal */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Invite a Friend
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendInvite} className="space-y-4">
            <div>
              <Label className="text-[#3D405B]">Their Name *</Label>
              <Input
                value={inviteData.name}
                onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                placeholder="Friend's name"
                required
                data-testid="invite-name"
              />
            </div>
            <div>
              <Label className="text-[#3D405B]">Their Email *</Label>
              <Input
                type="email"
                value={inviteData.email}
                onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                placeholder="friend@example.com"
                required
                data-testid="invite-email"
              />
            </div>
            <div>
              <Label className="text-[#3D405B]">Personal Message</Label>
              <Textarea
                value={inviteData.message}
                onChange={(e) => setInviteData({ ...inviteData, message: e.target.value })}
                className="bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl"
                placeholder="Add a personal note..."
                data-testid="invite-message"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowInvite(false)} className="flex-1 rounded-full border-[#F4F1EA]">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full" data-testid="send-invite-button">
                <Mail className="w-4 h-4 mr-2" />
                Send Invite
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Import Modal */}
      <Dialog open={showCSVImport} onOpenChange={setShowCSVImport}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Import Contacts
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCSVImport} className="space-y-4">
            <div className="p-6 border-2 border-dashed border-[#F4F1EA] rounded-2xl text-center">
              <FileText className="w-12 h-12 text-[#6D6F7C] mx-auto mb-3" />
              <p className="text-[#3D405B] font-medium mb-2">Upload your CSV file</p>
              <p className="text-sm text-[#6D6F7C] mb-4">
                Columns: Name, Phone, Email, Birthday, Notes
              </p>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files[0])}
                className="bg-white border-2 border-[#F4F1EA] rounded-xl"
                data-testid="csv-file-input"
              />
              {csvFile && (
                <p className="mt-2 text-sm text-[#81B29A]">
                  Selected: {csvFile.name}
                </p>
              )}
            </div>
            <div className="bg-[#F4F1EA] rounded-xl p-4">
              <p className="text-sm text-[#6D6F7C]">
                <strong>Tips:</strong>
                <br />• First row should contain headers
                <br />• Supported formats: Name, Phone, Email, Birthday
                <br />• Duplicates will be skipped automatically
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => { setShowCSVImport(false); setCsvFile(null); }} 
                className="flex-1 rounded-full border-[#F4F1EA]"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={!csvFile || isImporting}
                className="flex-1 bg-[#81B29A] hover:bg-[#71A28A] text-white rounded-full" 
                data-testid="import-csv-button"
              >
                {isImporting ? (
                  <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" /> Importing...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Import</>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Referral Code Modal */}
      <Dialog open={showReferral} onOpenChange={setShowReferral}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              <Share2 className="w-6 h-6 inline mr-2 text-[#E07A5F]" />
              Referral & Codes
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Your Referral Code */}
            <div className="p-4 bg-gradient-to-r from-[#E07A5F]/10 to-[#F2CC8F]/10 rounded-2xl">
              <p className="text-sm text-[#6D6F7C] mb-2">Your Referral Code</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white px-4 py-3 rounded-xl text-lg font-mono font-bold text-[#E07A5F] text-center">
                  {referralCode?.code || "Loading..."}
                </code>
                <Button 
                  onClick={copyReferralCode}
                  variant="outline" 
                  className="border-[#F4F1EA] rounded-xl"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-[#6D6F7C] mt-2">
                Share this code! Both you and your friend get 1 month of Plus free.
              </p>
              {referralCode?.uses > 0 && (
                <Badge className="mt-2 bg-[#81B29A]/20 text-[#81B29A]">
                  {referralCode.uses} friends joined!
                </Badge>
              )}
            </div>

            {/* Redeem a Code */}
            <div className="border-t border-[#F4F1EA] pt-4">
              <p className="text-sm font-medium text-[#3D405B] mb-2">Have a code?</p>
              <div className="flex gap-2">
                <Input
                  value={redeemCode}
                  onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                  placeholder="Enter invite or referral code"
                  className="bg-white border-[#F4F1EA] rounded-xl uppercase"
                />
                <Button 
                  onClick={handleRedeemCode}
                  className="bg-[#81B29A] hover:bg-[#71A28A] text-white rounded-xl"
                >
                  Redeem
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Snooze Nudge Modal */}
      <Dialog open={showSnoozeModal} onOpenChange={(open) => { setShowSnoozeModal(open); if (!open) setSelectedNudge(null); }}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              <Clock className="w-6 h-6 inline mr-2 text-[#E07A5F]" />
              Snooze Reminder
            </DialogTitle>
          </DialogHeader>
          
          {selectedNudge && (
            <div className="space-y-4">
              <p className="text-[#6D6F7C]">
                Snooze reminder to call <strong className="text-[#3D405B]">{selectedNudge.contact_name}</strong>?
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  className="border-[#F4F1EA] rounded-xl py-6 hover:bg-[#F4F1EA]"
                  onClick={() => handleSnoozeNudge(selectedNudge.nudge_id, 1)}
                  data-testid="snooze-1h"
                >
                  <div className="text-center">
                    <p className="text-lg font-bold text-[#3D405B]">1 Hour</p>
                    <p className="text-xs text-[#6D6F7C]">Quick break</p>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="border-[#F4F1EA] rounded-xl py-6 hover:bg-[#F4F1EA]"
                  onClick={() => handleSnoozeNudge(selectedNudge.nudge_id, 4)}
                  data-testid="snooze-4h"
                >
                  <div className="text-center">
                    <p className="text-lg font-bold text-[#3D405B]">4 Hours</p>
                    <p className="text-xs text-[#6D6F7C]">Later today</p>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="border-[#F4F1EA] rounded-xl py-6 hover:bg-[#F4F1EA]"
                  onClick={() => handleSnoozeNudge(selectedNudge.nudge_id, 24)}
                  data-testid="snooze-24h"
                >
                  <div className="text-center">
                    <p className="text-lg font-bold text-[#3D405B]">Tomorrow</p>
                    <p className="text-xs text-[#6D6F7C]">24 hours</p>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="border-[#F4F1EA] rounded-xl py-6 hover:bg-[#F4F1EA]"
                  onClick={() => handleSnoozeNudge(selectedNudge.nudge_id, 72)}
                  data-testid="snooze-3d"
                >
                  <div className="text-center">
                    <p className="text-lg font-bold text-[#3D405B]">3 Days</p>
                    <p className="text-xs text-[#6D6F7C]">This week</p>
                  </div>
                </Button>
                {/* 7-day snooze option - useful for missed touch nudges */}
                <Button
                  variant="outline"
                  className="col-span-2 border-[#F4F1EA] rounded-xl py-6 hover:bg-[#F4F1EA]"
                  onClick={() => handleSnoozeNudge(selectedNudge.nudge_id, 168)}
                  data-testid="snooze-7d"
                >
                  <div className="text-center">
                    <p className="text-lg font-bold text-[#3D405B]">1 Week</p>
                    <p className="text-xs text-[#6D6F7C]">Remind me next week</p>
                  </div>
                </Button>
              </div>
              
              <div className="pt-2">
                <Button
                  variant="ghost"
                  className="w-full text-[#6D6F7C] hover:text-[#E07A5F]"
                  onClick={() => handleDismissNudge(selectedNudge.nudge_id)}
                >
                  <X className="w-4 h-4 mr-2" />
                  Dismiss this reminder
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* App Footer with Legal Links */}
      <footer className="mt-auto py-6 px-4 bg-white/50 border-t border-[#F4F1EA]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-[#6D6F7C]">
          <p>© 2025 MidVantage Labs</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-[#E07A5F] transition-colors" data-testid="dashboard-terms-link">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-[#E07A5F] transition-colors" data-testid="dashboard-privacy-link">
              Privacy
            </Link>
          </div>
        </div>
      </footer>

      {/* Post-Call Logging Prompt */}
      <PostCallPrompt
        onLogCall={handlePostCallLog}
        lastCallLoggedAt={lastCallLoggedAt}
      />
    </div>
  );
};

export default Dashboard;
